import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/utils/apiClient';
import { filterOnBallotElections } from '@/utils/ballotStatus';
import useGuestAddress from './useGuestAddress';

/**
 * Fetches the personalized ballot for a guest's locally-cached address.
 *
 * Triggers a POST to the unauthenticated ``/ballot/by-address`` endpoint
 * whenever the guest's stored ``formatted_address`` changes, and returns
 * the same ``ballot_user`` shape that :func:`useInitialBallot` produces
 * for signed-in users. Used as the third data source for
 * :func:`SampleBallotView` (alongside personal-auth and national).
 *
 * Only fires when a guest address is present. Signed-in users continue
 * to use :func:`useInitialBallot` and authenticated users without a
 * saved address fall through to the national view; this hook stays a
 * no-op for both.
 */
const useGuestPersonalBallot = () => {
  const { t } = useTranslation('ballot');
  const { address } = useGuestAddress();
  const [ballot, setBallot] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stores the most-recently-requested address. Used both to dedupe
  // re-emits of the same address (effect below) and to invalidate
  // resolved responses for addresses the user has already moved past
  // (last-write-wins guard inside fetchGuestBallot).
  const latestRequestedAddressRef = useRef<string | null>(null);

  const fetchGuestBallot = useCallback(
    async (formattedAddress: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient('/ballot/by-address', {
          method: 'POST',
          body: JSON.stringify({ address: formattedAddress }),
        });
        const data: { success: boolean; ballot_user?: Record<string, unknown> } =
          await response.json();
        // Last-write-wins guard: if the user changed addresses (A → B) while
        // A's request was in flight, ignore A's response when it resolves.
        // Without this, A's response could clobber B's if A is slower.
        if (latestRequestedAddressRef.current !== formattedAddress) {
          return;
        }
        if (data.success && data.ballot_user) {
          // The one place this payload enters the app: a `notOnBallot` measure
          // or `withdrawn` candidate from an older backend never renders,
          // counts, or reaches the guest chat context.
          setBallot(filterOnBallotElections(data.ballot_user));
        } else {
          setError(t('error.lookupNoData'));
        }
      } catch (err) {
        if (latestRequestedAddressRef.current !== formattedAddress) {
          return;
        }
        console.error('Guest ballot fetch failed:', err);
        setError(t('error.fetchFailed'));
      } finally {
        if (latestRequestedAddressRef.current === formattedAddress) {
          setIsLoading(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    if (!address?.formatted_address) {
      setBallot(null);
      setError(null);
      latestRequestedAddressRef.current = null;
      return;
    }
    // Avoid redundant refetches when the same address is re-emitted
    // (e.g. unrelated React-tree updates trigger a re-read of localStorage).
    if (latestRequestedAddressRef.current === address.formatted_address) return;
    latestRequestedAddressRef.current = address.formatted_address;
    // A new address: drop the old one's ballot so the view shows its loading
    // state instead of the previous address's races while this one loads.
    setBallot(null);
    fetchGuestBallot(address.formatted_address);
  }, [address?.formatted_address, fetchGuestBallot]);

  const refetch = useCallback(() => {
    if (address?.formatted_address) {
      latestRequestedAddressRef.current = null;
      latestRequestedAddressRef.current = address.formatted_address;
      fetchGuestBallot(address.formatted_address);
    }
  }, [address?.formatted_address, fetchGuestBallot]);

  return { ballot, isLoading, error, refetch };
};

export default useGuestPersonalBallot;
